// ============================================================
// Full path:
//   D:\Thyroid Consultation Software\ThyroConsult Frontend\thyroconsult-frontend\src\utils\passwordPolicy.js
//
// Password rule for new and reset passwords (1 Oct 2026), following NIST
// SP 800-63B Rev. 4: length matters, character-mix rules do not.
//   • at least 12 characters, at most 128 (long passphrases welcome)
//   • no "must contain an uppercase letter / number / symbol" rules
//   • refused when it is a common or easily guessed password, a simple
//     sequence or repeated character, or contains the person's own name,
//     mobile number or email name
// Existing passwords keep working — this applies only when a password is
// set or changed.
//
// The server applies the same rule (thyroconsult-backend\src\utils\
// passwordPolicy.js) — keep the two in step. This copy only gives the
// person the message straight away, before anything is sent.
// ============================================================

export const MIN_LENGTH = 12;
export const MAX_LENGTH = 128;

export const PASSWORD_RULE_MESSAGE =
  'Please use at least 12 characters. A short phrase of three or four words is easiest to remember. '
  + 'Avoid common passwords and your own name, mobile number or email.';

// Words that make a password easy to guess when padded with numbers or
// symbols (e.g. "Password@2026", "welcome123456", "thyroconsult1").
const COMMON_WORDS = new Set([
  'password', 'passw0rd', 'pass', 'welcome', 'qwerty', 'qwertyuiop', 'asdfgh', 'asdfghjkl', 'zxcvbnm',
  'admin', 'administrator', 'login', 'letmein', 'iloveyou', 'loveyou', 'love', 'india', 'bharat',
  'mumbai', 'delhi', 'pune', 'chennai', 'kolkata', 'bangalore', 'hyderabad', 'thyroid', 'thyroconsult',
  'agitomed', 'doctor', 'patient', 'hospital', 'clinic', 'health', 'secret', 'monkey', 'dragon',
  'sunshine', 'princess', 'football', 'cricket', 'baseball', 'superman', 'batman', 'shadow', 'master',
  'hello', 'freedom', 'whatever', 'trustno', 'abc', 'abcd', 'abcdef', 'test', 'testing', 'user',
  'changeme', 'default', 'computer', 'internet', 'samsung', 'iphone', 'google', 'facebook', 'krishna',
  'ganesh', 'jaishreeram', 'shreeram', 'omnamahshivaya', 'jaihind', 'mother', 'father', 'family',
]);

// Exact passwords seen in breach lists that are 12+ characters long.
const COMMON_PASSWORDS = new Set([
  '123456789012', '1234567890123', '12345678901234', '123123123123', '111111111111', '000000000000',
  '1q2w3e4r5t6y', '1qaz2wsx3edc', 'qwertyuiop12', 'qwertyuiop123', 'qwerty123456', 'asdfghjkl123',
  'password1234', 'password12345', 'password123456', 'iloveyou1234', 'welcome12345', 'admin1234567',
  'abcd12345678', 'abc123456789', 'aaaaaaaaaaaa', 'qwertyqwerty', 'passwordpassword', 'letmeinletmein',
]);

const SEQUENCES = [
  '0123456789012345678901234567890',
  '9876543210987654321098765432109',
  'abcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyz',
  'zyxwvutsrqponmlkjihgfedcbazyxwvutsrqponmlkjihgfedcba',
  'qwertyuiopasdfghjklzxcvbnmqwertyuiopasdfghjklzxcvbnm',
];

// '' when acceptable, otherwise a message to show the person.
// personal: values from the person's own details (name, mobile, email).
export function passwordProblem(password, { personal = [] } = {}) {
  if (typeof password !== 'string' || password.length < MIN_LENGTH) return PASSWORD_RULE_MESSAGE;
  if (password.length > MAX_LENGTH) return `Please use no more than ${MAX_LENGTH} characters.`;

  const lower = password.toLowerCase();
  const compact = lower.replace(/\s+/g, '');
  const letters = lower.replace(/[^a-z]/g, '');

  if (COMMON_PASSWORDS.has(lower) || COMMON_PASSWORDS.has(compact)) {
    return 'This password is too common and easy to guess. Please choose another.';
  }
  // A common word padded only with numbers / symbols
  if (letters && COMMON_WORDS.has(letters)) {
    return 'This password is based on a common word and is easy to guess. Please choose another.';
  }
  if (/^(.)\1+$/.test(compact)) return 'Please do not repeat the same character. Choose another password.';
  if (SEQUENCES.some(seq => seq.includes(compact))) {
    return 'Please do not use a simple sequence such as 123456789012 or abcdefghijkl.';
  }

  for (const raw of personal) {
    if (!raw) continue;
    const value = String(raw).toLowerCase();
    const candidates = [];
    if (value.includes('@')) candidates.push(value.split('@')[0]);   // email name
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 6) candidates.push(digits.slice(-10));        // mobile number
    if (!value.includes('@') && /[a-z]/.test(value)) candidates.push(value.replace(/\s+/g, '')); // a name
    for (const c of candidates) {
      if (c && c.length >= 4 && compact.includes(c)) {
        return 'Please do not use your own name, mobile number or email in your password.';
      }
    }
  }
  return '';
}

// Placeholder / hint text for password boxes
export const PASSWORD_HINT = 'At least 12 characters — e.g. three or four words';
